// Edits one metadata field by its type, as the Mac's FieldEditor: a menu of options (with their colours) or of the
// cast (characters for POV, places for the setting, wearing their colours), a number or a line of text that saves on
// Return or on leaving it, a switch, a date (with a button to clear it); story times are shown, not edited. Used in the inspector and, compact,
// in the outline's cells. Also the commit-on-leave text fields the inspector's title, synopsis and notes use.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addingTag, hasTag, joinTags, parseTags, removingTag, tagKey, TAGS_FIELD, type Entity, type Field, type MetadataValue, type OpenedProject, type Project, type TagCount,
} from '@writing-suite/core';
import { showTag } from '../store/plan.ts';
import { projectTags } from '../store/planModel.ts';

/** The Tags field a project made on the web hasn't got yet (the field is added with the first tag): shown anyway. */
export const VIRTUAL_TAGS_FIELD: Field = { id: TAGS_FIELD, name: 'Tags', type: 'tags', options: [] };
import { cssColor } from './helpers.ts';
import { Dot, MenuButton, type MenuEntry } from './Menu.tsx';
import { numberInput, wordTargetInput } from './structure.ts';

/** The option or entity a text value names. */
export function optionOf(field: Field, entities: Entity[], value: MetadataValue | undefined): { id: string; name: string; color: string | null } | null {
  if (value?.type !== 'text' || !value.value) return null;
  const option = field.options.find((o) => o.id === value.value || o.name === value.value);
  if (option) return { id: option.id, name: option.name, color: cssColor(option.color) };
  // An option the writer took out reads as none, never as its id.
  if (field.type === 'enum') return null;
  const entity = entities.find((e) => e.id === value.value);
  if (entity) return { id: entity.id, name: entity.name, color: entity.kind === 'character' ? cssColor(entity.color) : null };
  return { id: value.value, name: value.value, color: null };
}

/** The entities an entity-ref field offers: its kind's (POV → characters, setting → places), else everyone. */
export function castFor(field: Field, entities: Entity[]): Entity[] {
  return entities
    .filter((e) => !field.entityKind || e.kind === field.entityKind)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
}

/** Menu entries choosing a field's value: None, then its options (or the cast). */
export function choiceEntries(field: Field, entities: Entity[], value: MetadataValue | undefined, choose: (v: MetadataValue | null) => void): MenuEntry[] {
  const chosen = optionOf(field, entities, value);
  const options =
    field.type === 'entity-ref'
      ? castFor(field, entities).map((e) => ({ id: e.id, name: e.name, color: e.kind === 'character' ? cssColor(e.color) : null }))
      : field.options.map((o) => ({ id: o.id, name: o.name, color: cssColor(o.color) }));
  const withColours = options.some((o) => o.color);
  return [
    { kind: 'radio', label: 'None', checked: !chosen, onSelect: () => choose(null), ...(withColours ? { color: null } : {}) },
    ...options.map((o): MenuEntry => ({
      kind: 'radio', label: o.name, checked: chosen?.id === o.id, onSelect: () => choose({ type: 'text', value: o.id }), ...(withColours ? { color: o.color } : {}),
    })),
  ];
}

export function FieldEditor({
  field,
  value,
  project,
  entities,
  onChange,
  inline = false,
  readOnly = false,
  id,
  emptyText,
}: {
  field: Field;
  value: MetadataValue | undefined;
  project: Project;
  entities: Entity[];
  onChange: (value: MetadataValue | null) => void;
  inline?: boolean;
  readOnly?: boolean;
  id?: string;
  /** What a compact list cell reads with nothing chosen ("No beat"), faint; else a dash. */
  emptyText?: string;
}) {
  const shown = project.displayValue(field.id, value);
  // The project's tags in use, for the + menu (rebuilt only when the project does).
  const opened = project as OpenedProject;
  const tagsInUse = useMemo(() => (field.type === 'tags' && opened.record ? projectTags(opened) : []), [field.type, opened]);
  if (field.type === 'tags') {
    return (
      <TagsEditor id={id} label={field.name} value={value} others={tagsInUse} inline={inline} readOnly={readOnly}
        onChange={(tags) => onChange(tags.length ? { type: 'text', value: joinTags(tags) } : null)} />
    );
  }
  if (field.type === 'date' && !readOnly) {
    return <DateInput id={id} label={field.name} value={value?.type === 'date' ? value.value : null} onChange={(d) => onChange(d ? { type: 'date', value: d } : null)} />;
  }
  if (readOnly || field.type === 'date' || field.type === 'story-time') {
    return (
      <span className={`field-value${shown ? '' : ' none'}`} id={id}>
        {field.type === 'boolean' ? (value?.type === 'boolean' && value.value ? 'Yes' : inline ? '' : 'No') : shown || (inline ? '' : '—')}
      </span>
    );
  }
  switch (field.type) {
    case 'enum':
    case 'entity-ref': {
      if (field.type === 'enum' && field.options.length === 0) break;
      const chosen = optionOf(field, entities, value);
      return (
        <MenuButton
          label={field.name}
          className={`option-button${inline ? ' inline' : ''}`}
          title={`${field.name}: ${chosen?.name ?? 'None'}`}
          entries={() => choiceEntries(field, entities, value, onChange)}
        >
          {chosen?.color ? <Dot color={chosen.color} /> : null}
          <span className={chosen ? 'option-name' : emptyText ? 'option-name none empty-text' : 'option-name none'}>{chosen?.name ?? emptyText ?? (inline ? '—' : 'None')}</span>
          <span className="option-chevron" aria-hidden="true" />
        </MenuButton>
      );
    }
    case 'boolean':
      return (
        <input
          type="checkbox"
          className="field-switch"
          id={id}
          aria-label={inline ? field.name : undefined}
          checked={value?.type === 'boolean' && value.value}
          onChange={(e) => onChange({ type: 'boolean', value: e.target.checked })}
        />
      );
    case 'number':
      return (
        <CommitInput
          id={id}
          label={inline ? field.name : undefined}
          value={value?.type === 'number' ? String(value.value) : value?.type === 'text' ? value.value : ''}
          numeric
          inline={inline}
          onCommit={(text) => onChange(field.id === 'word-target' ? wordTargetInput(text) : numberInput(text))}
        />
      );
    default:
      break;
  }
  return (
    <CommitInput
      id={id}
      label={inline ? field.name : undefined}
      value={value?.type === 'text' ? value.value : shown}
      inline={inline}
      onCommit={(text) => {
        const t = text.trim();
        onChange(t ? { type: 'text', value: t } : null);
      }}
    />
  );
}

/** A line of text that saves when you press Return or leave it, not on every keystroke; Escape puts it back. */
export function CommitInput({
  value,
  onCommit,
  label,
  id,
  numeric = false,
  inline = false,
  className,
}: {
  value: string;
  onCommit: (text: string) => void;
  label?: string;
  id?: string;
  numeric?: boolean;
  inline?: boolean;
  className?: string;
}) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  useEffect(() => void (!focused.current && setDraft(value)), [value]);
  const save = () => {
    if (draft !== value) onCommit(draft);
  };
  return (
    <input
      id={id}
      className={className ?? `field-input${inline ? ' inline' : ''}${numeric ? ' numeric' : ''}`}
      aria-label={label}
      inputMode={numeric ? 'decimal' : undefined}
      placeholder={inline ? '—' : 'None'}
      value={draft}
      onFocus={() => (focused.current = true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        focused.current = false;
        save();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          save();
        } else if (e.key === 'Escape') {
          setDraft(value);
        }
      }}
    />
  );
}

/** A text area that saves a second after typing stops, or on leaving it; another device's change shows unless it's being
 *  typed in. */
export function CommitTextArea({
  value,
  onCommit,
  id,
  rows = 3,
  className,
  readOnly,
  placeholder,
  'aria-label': label,
}: {
  value: string;
  onCommit: (text: string) => void;
  id?: string;
  rows?: number;
  className?: string;
  readOnly?: boolean;
  placeholder?: string;
  'aria-label'?: string;
}) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ draft, value, onCommit });
  latest.current = { draft, value, onCommit };
  useEffect(() => void (!focused.current && setDraft(value)), [value]);
  const commit = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const l = latest.current;
    if (l.draft !== l.value) l.onCommit(l.draft);
  };
  // Leaving the page (or the item) saves what's typed.
  useEffect(() => () => commit(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea
      id={id}
      className={className}
      rows={rows}
      value={draft}
      readOnly={readOnly}
      placeholder={placeholder}
      aria-label={label}
      onFocus={() => (focused.current = true)}
      onChange={(e) => {
        setDraft(e.target.value);
        latest.current.draft = e.target.value;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(commit, 1000);
      }}
      onBlur={() => {
        focused.current = false;
        commit();
      }}
    />
  );
}

/** A tag as a capsule: choosing it shows everything tagged with it (one-click filtering); with `onRemove`, an × takes
 *  it off the document. */
export function TagChip({ tag, onRemove }: { tag: string; onRemove?: () => void }) {
  return (
    <span className="tag-chip" data-tag={tag}>
      <button
        type="button"
        className="tag-name"
        onClick={(e) => {
          // On a corkboard card the chip sits inside the card's link: the tag, not the document.
          e.preventDefault();
          e.stopPropagation();
          showTag(tag);
        }}
        title={`Show everything tagged “${tag}”`}
      >
        {tag}
      </button>
      {onRemove ? (
        <button type="button" className="tag-remove" onClick={onRemove} aria-label={`Remove ${tag}`}>
          ×
        </button>
      ) : null}
    </span>
  );
}

/** A document's tags (the Mac's TagsEditor): chips with an ×, a field that takes another on Return (or several with
 *  commas), and a + menu of the project's other tags with their counts. Inline (an outline cell): chips and the menu. */
export function TagsEditor({
  value,
  others,
  onChange,
  inline = false,
  readOnly = false,
  id,
  label,
}: {
  value: MetadataValue | undefined;
  /** The project's tags in use (`projectTags`), for the + menu. */
  others: readonly TagCount[];
  onChange: (tags: string[]) => void;
  inline?: boolean;
  readOnly?: boolean;
  id?: string;
  label?: string;
}) {
  const [draft, setDraft] = useState('');
  const tags = value?.type === 'text' ? parseTags(value.value) : [];
  const offered = others.filter((o) => !hasTag(tags, o.tag));
  const add = () => {
    const more = parseTags(draft);
    setDraft('');
    if (more.length) onChange(addingTag(joinTags(more), tags));
  };
  return (
    <div className={`tags-editor${inline ? ' inline' : ''}`} data-tags={tags.length}>
      <div className="tag-chips">
        {tags.map((tag) => (
          <TagChip key={tagKey(tag)} tag={tag} onRemove={readOnly ? undefined : () => onChange(removingTag(tag, tags))} />
        ))}
        {!readOnly && offered.length ? (
          <MenuButton
            label="Add one of the project’s tags"
            className="tag-add"
            title="Add one of the project’s tags"
            entries={() => offered.map((o): MenuEntry => ({ label: `${o.tag}  ·  ${o.count}`, onSelect: () => onChange(addingTag(o.tag, tags)) }))}
          >
            +
          </MenuButton>
        ) : null}
        {tags.length === 0 && (readOnly || inline) ? <span className="field-value none">—</span> : null}
      </div>
      {!readOnly && !inline ? (
        <input
          id={id}
          className="field-input tag-input"
          aria-label={label ?? 'Add a tag'}
          placeholder="Add a tag"
          value={draft}
          autoCapitalize="none"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={add}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            } else if (e.key === 'Escape') setDraft('');
          }}
        />
      ) : null}
    </div>
  );
}

/** A date field (`DateFieldEditor` on the Mac): the browser's date picker, a day in this device's zone, and a button to
 *  clear it. */
function DateInput({ id, label, value, onChange }: { id?: string; label: string; value: Date | null; onChange: (d: Date | null) => void }) {
  const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return (
    <span className="date-input">
      <input
        type="date"
        id={id}
        aria-label={label}
        value={value ? day(value) : ''}
        onChange={(e) => {
          const [y, m, d] = e.target.value.split('-').map(Number);
          onChange(e.target.value && y ? new Date(y, m - 1, d) : null);
        }}
      />
      {value ? (
        <button type="button" className="link-button clear-date" aria-label={`Clear ${label}`} onClick={() => onChange(null)}>
          ×
        </button>
      ) : null}
    </span>
  );
}

